// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { isoDateSchema } from '../common.js';
// Workforce controls §5 (customer request #1, "My statement"): an employee's statement of leave and deductions over a
// period — the portal's /my/statement (GET /orgs/:orgId/me/statement) and the Statement tab of the employee profile
// (GET /orgs/:orgId/employees/:id/statement). Every figure is read from what the rest of the product already stores:
// leave records (the ONE leave-day rule, clipped to the period), the short leave requests (hourly permissions, official
// assignments, nursing hours… — migration 20261019000310), the balances function, the active day marks the pay-effect charger
// writes, and the daily records.
/** The longest period one statement covers (a leap year). */
export const STATEMENT_MAX_DAYS = 366;
const utcDay = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d)
        return null;
    const t = Date.UTC(y, m - 1, d);
    const back = new Date(t);
    return back.getUTCFullYear() === y && back.getUTCMonth() === m - 1 && back.getUTCDate() === d ? t : null;
};
const checkPeriod = (v, ctx) => {
    const a = utcDay(v.from);
    const b = utcDay(v.to);
    if (a === null) {
        ctx.addIssue({ code: 'custom', path: ['from'], message: 'Not a calendar date' });
        return;
    }
    if (b === null) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Not a calendar date' });
        return;
    }
    if (b < a)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'to must be on/after from' });
    else if ((b - a) / 86_400_000 + 1 > STATEMENT_MAX_DAYS)
        ctx.addIssue({ code: 'custom', path: ['to'], message: `A statement covers at most ${STATEMENT_MAX_DAYS} days` });
};
/** `from` – `to` (inclusive calendar dates, the organisation's dates): `to` on or after `from`, at most 366 days. */
export const statementQuerySchema = z.object({ from: isoDateSchema, to: isoDateSchema }).superRefine(checkPeriod);
/** The languages the statement's CSV is written in (its headings and labels; names keep the record's own spelling). */
export const STATEMENT_EXPORT_LOCALES = ['en', 'ar'];
/**
 * GET /orgs/:orgId/me/statement/export and GET /orgs/:orgId/employees/:id/statement/export: the statement of the same period as
 * a CSV built by the API (`CsvExportFileDto`) — the GET's authorization and sections, formula-escaped cells, audited with its
 * row count and period, under a per-organisation hourly quota. `locale` picks the language of the headings (English by default).
 */
export const statementExportQuerySchema = z.object({ from: isoDateSchema, to: isoDateSchema, locale: z.enum(STATEMENT_EXPORT_LOCALES).optional() }).superRefine(checkPeriod);
/**
 * A leave entry of the period. `kind`: REQUEST — leave the employee (or HR for them) applied for, or imported from the HR
 * system; DEDUCTION — the single-day paid leave the pay-effect charger booked for an unexcused day (the deduction table
 * lists the same day as LEAVE_CHARGED). `days` is what the whole leave charges, `daysInPeriod` the part inside the period.
 */
export const STATEMENT_LEAVE_KINDS = ['REQUEST', 'DEDUCTION'];
/**
 * A deduction of the period, from the active day marks: LEAVE_CHARGED (a PAY_EFFECT mark — paid leave of `leaveTypeCode`
 * charged for an unexcused day), LOP (loss of pay), UNEXCUSED (the day was marked unexcused; `days` = its pay effect, which
 * the LEAVE_CHARGED or LOP entry of the same day settles — it is not added to the deduction days).
 */
export const STATEMENT_DEDUCTION_KINDS = ['LOP', 'LEAVE_CHARGED', 'UNEXCUSED'];
//# sourceMappingURL=statement.js.map